<script setup lang="ts">
import type { SecondStepProps } from '.';
import yup from '@/core/validation';
import messages from './SecondStep.locale.json';
import { useEvents } from '~/composables/events/useEvents';
import {
	MixpanelProps,
	MixpanelUserActionValues,
	MixpanelLoginTypeValues
} from '~/core/service/mixpanel';

const { trackSignAndLogin } = useEvents();
const { t } = useI18n({
	useScope: 'local',
	messages
});

const props = defineProps<SecondStepProps>();

const emit = defineEmits<{
	(e: 'validated', value: string): void;
}>();

const schema = yup.object({
	phone: yup
		.string()
		.phone('')
		.when((_, schema) => (props.method === 'sms' ? schema.required() : schema))
});

const { defineField, handleSubmit, errors, resetForm } = useForm({
	validationSchema: schema
});

const [phone, phoneAttrs] = defineField('phone', {
	validateOnModelUpdate: false
});

const submit = handleSubmit(() => {
	emit('validated', phone.value);
});

const route = useRoute();
const { user } = useUserSession();

onMounted(() => {
	trackSignAndLogin.trackLoginPhone({
		[MixpanelProps.CURRENT_PAGE]: route.fullPath,
		[MixpanelProps.USER_NAME]: user.value?.name || '',
		[MixpanelProps.USER_EMAIL]: user.value?.email || '',
		[MixpanelProps.USER_PHONE]: user.value?.phoneNumber || '',
		[MixpanelProps.USER_ACTION]: MixpanelUserActionValues.SIGN_UP,
		[MixpanelProps.LOGIN_TYPE]: MixpanelLoginTypeValues.PHONE
	});
});

onUnmounted(() => {
	resetForm();
});
</script>

<template>
	<section class="space-y-6" data-testid="second-step">
		<InputPhone
			v-model="phone"
			:autofocus="isActive"
			v-bind="phoneAttrs"
			:error="errors?.phone"
			select-trigger-class="bg-white-dark-1"
			@keyup.enter="submit"
		/>

		<Button data-testid="submit-otp-login-button" class="h-12" @click="submit">
			{{ t('button') }}
		</Button>
	</section>
</template>
